<script setup lang="ts">

import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { computed } from 'vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { CDN } from '@/models/cdn.ts'
import ModalTrigger from '@/components/Interface/Modal/ModalTrigger.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useShipDesignStore } from '@/stores/shipDesignStore.ts'
import Backend from '@/models/backend.ts'
import BuildingEffects from '@/components/Sections/Planets/PlanetTabs/Generic/BuildingEffects.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { pluralize } from '@/models/tools.ts'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore()
const shipDesignStore = useShipDesignStore()
const assetStore = useAssetStore()
const gameStore = useGameStore()
const backend = new Backend()
const fleetStore = useFleetStore()

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)
const shipDesignData = computed(() => shipDesignStore.getShipDesignById(planetData.value.productionShipDesignId))
const productionObjectData = computed(() => assetStore.findProductionObjectById(planetData.value.productionObjectId))

// Doak Minister - Production to Food conversion
const hasDoakMinister = computed(() => gameStore.playerMinisters.has('production_converted_to_food'))
const isDoakActive = computed(() => {
  const minister = gameStore.playerMinisters.get('production_converted_to_food')
  return minister && minister.fired !== 1
})
const isConvertingToFood = computed(() => 
  planetData.value.productionObjectId === 0 && hasDoakMinister.value && isDoakActive.value
)

const widthPercentage = computed(() => {
  let progress = 0;
  if (shipDesignData.value) {
    progress = (planetData.value.productionProgress / shipDesignData.value.productionCost);
  } else {
    progress = (planetData.value.productionProgress / productionObjectData.value.productionCost)
  }
  if(progress > 1) {
    progress = 1
  }
  return Math.floor(progress * 100)
});

const weeksLeft = computed(() =>
{
  let turns = 0;
  if (shipDesignData.value) {
    turns = (shipDesignData.value.productionCost - planetData.value.productionProgress)/planetData.value.productionPerTurn
  } else {
    turns = (productionObjectData.value.productionCost - planetData.value.productionProgress) / planetData.value.productionPerTurn
  }
  if(turns < 0) {
    turns = 0
  }
  return Math.ceil(turns)
});

const name = computed(() => {
  if(planetData.value.productionObjectId === 0) {
    return t('planets.production.current.planetProduction')
  }
  if (shipDesignData.value) {
    return shipDesignData.value.name
  } else if (productionObjectData.value?.type === 'building') {
    return t(`buildings.buildings.${productionObjectData.value.objectId}.name`)
  } else {
    return t('planets.production.ship.name')
  }
});

const buildingData = computed(() => {
  return assetStore.findBuildingByCode(productionObjectData.value.objectId)
})

const constructionCost = computed(() => {
  if (shipDesignData.value) {
    return shipDesignData.value.productionCost
  } else {
    return productionObjectData.value.productionCost
  }
});

// Get full queue including current production
const fullQueue = computed(() => {
  const items = [];

  // Add current production as first item if it exists
  if (planetData.value.productionObjectId > 0) {
    items.push({
      id: 0,
      productionObjectId: planetData.value.productionObjectId,
      productionShipDesignId: planetData.value.productionShipDesignId,
      productionShipFleetId: planetData.value.productionShipFleetId
    });
  }

  // Add queued items
  if (planetData.value.productionQueue) {
    items.push(...planetData.value.productionQueue);
  }

  return items;
});

// Current is always at position 0
const canMoveDown = computed(() => {
  return fullQueue.value.length > 1;
});

/**
 * Methods
 */
const cancelProduction = () => {
  backend.cancelPlanetProductionObject(planetData.value.id);
  gameStore.setTinySuccessToastMessage(t('planets.production.current.cancelled'));
}

const moveDown = () => {
  const currentQueue = [...fullQueue.value];

  if (currentQueue.length > 1) {
    [currentQueue[0], currentQueue[1]] = [currentQueue[1], currentQueue[0]];
    reorderQueue(currentQueue);
  }
}

const moveToBottom = () => {
  const currentQueue = [...fullQueue.value];

  if (currentQueue.length > 1) {
    const item = currentQueue.shift()!;
    currentQueue.push(item);
    reorderQueue(currentQueue);
  }
}

const reorderQueue = async (reorderedQueue: any[]) => {
  const apiQueue = reorderedQueue.map(item => ({
    id: item.id,
    production_object_id: item.productionObjectId,
    production_ship_design_id: item.productionShipDesignId,
    production_ship_fleet_id: item.productionShipFleetId
  }));

  try {
    await backend.reorderPlanetProductionQueue(planetData.value.id, apiQueue);
    gameStore.setTinySuccessToastMessage(t('planets.production.current.reordered'));
  } catch (error) {
    gameStore.setTinyErrorToastMessage(t('planets.production.current.reorderFailed'));
    logger.error('Failed to reorder production queue:', error);
  }
}
</script>

<template>
  <StandardCard :title="name">
    <div class="flex flex-col"  v-if="planetData.productionObjectId>0">
      <!--- Image --->
      <div class="flex justify-between">
        <div class="flex-shrink-0">
          <ModalTrigger v-if="productionObjectData.type=='building'" type="building" :data="{'buildingCode': productionObjectData.objectId}">
            <img
              :src="CDN.building(128,productionObjectData.objectId)" class="rounded h-20" alt=""
            />
          </ModalTrigger>
          <img
            v-else-if="shipDesignData?.frameCode"
            :src="CDN.shipComponent(128, shipDesignData.frameCode)"
            class="rounded h-20"
            alt="">
        </div>
        <div class="flex flex-grow flex-col">
          <div class="flex-grow text-subtext ps-2">
            <span v-if="planetData.productionShipFleetId>0" class="pt-3" v-html="t('planets.production.current.buildShipForFleet', { fleetName: `<b class='text-slate-400'>${fleetStore.getFleetNameById(planetData.productionShipFleetId)}</b>` })"></span>
            <span v-else-if="productionObjectData?.type === 'building'">{{ t(`buildings.buildings.${productionObjectData.objectId}.description`) }}</span>
          </div>
          <!--- Icons --->
          <div class="flex justify-between">
            <div class="flex justify-start">
              <div class="flex items-center pt-1 ps-2">
                <BuildingEffects v-if="buildingData" :building="buildingData"/>
              </div>
            </div>
            <div class="flex items-end gap-1">
              <ActionButton
                @click.stop="moveDown"
                :disabled="!canMoveDown"
                :class="{ 'opacity-50 cursor-not-allowed': !canMoveDown }"
                :for-icon="true"
                :title="t('planets.production.queue.moveDown')"
              >
                <GenericIcon :tooltip="t('planets.production.queue.moveDown')" type="down" />
              </ActionButton>
              <ActionButton
                @click.stop="moveToBottom"
                :disabled="!canMoveDown"
                :class="{ 'opacity-50 cursor-not-allowed': !canMoveDown }"
                :for-icon="true"
                :title="t('planets.production.queue.moveToBottom')"
              >
                <GenericIcon :tooltip="t('planets.production.queue.moveToBottom')" type="bottom" />
              </ActionButton>
              <DeleteButton @click="cancelProduction" :for-icon="true">
                <GenericIcon :tooltip="t('planets.production.queue.delete')" type="trash" />
              </DeleteButton>
            </div>
          </div>
        </div>
      </div>
      <div class="pt-2 m-2 mt-3">
        <SimpleProgressBar :percentage="widthPercentage" color="blue" :height="5" :text="weeksLeft + ' ' + pluralize(weeksLeft, t('research.week'), t('research.weeks'))" :subtext="`${planetData.productionProgress} / ${constructionCost}`"/>
      </div>
    </div>
    <div v-else class="p-1 text-center text-subtext">
      <!-- Doak Minister - Production to Food Conversion -->
      <div v-if="isConvertingToFood" class="">
          <div class="flex items-top">
            <img :src="CDN.ministerImage('production_converted_to_food')" class="w-20 h-20 rounded mr-3" alt="Doak Minister"/>
            <div class="flex flex-col justify-start text-left">
              <div class="text-slate-400">{{ t('planets.production.current.doak.title') }}</div>
              <div class="">{{ t('planets.production.current.doak.converting') }}</div>
              <div class="flex justify-start pt-4">
                <GenericIcon type="production" class="mr-2"/>
                <div class="text-red-500 font-medium">{{ -planetData.productionPerTurn }}</div>
                <GenericIcon type="right" class="mx-3"/>
                <GenericIcon type="food" class="mr-2"/>
                <div class="text-green-400 font-medium">{{ planetData.productionPerTurn }}</div>
              </div>
            </div>
          </div>
      </div>

      <!-- Default message when no minister or regular idle state -->
      <div v-else>
        {{ t('planets.production.current.noProduction') }} <GenericIcon type="production"/> <GenericIcon type="cog"/>
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">

</style>